/**
 * Student Assignments - View and download assignments
 * Students can see published assignments in their classroom and download PDF attachments
 */

import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { 
  BookOpen, 
  Calendar, 
  Clock,
  FileText,
  Download,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface Assignment {
  id: string;
  classroom_name: string;
  subject_name: string;
  teacher_name: string;
  title: string;
  description?: string;
  due_at?: number;
  attachment_count: number;
  created_at: number;
  attachments?: Array<{
    id: string;
    file_name: string;
    size_bytes: number;
    created_at: number;
  }>;
}

export function StudentAssignments() {
  const { user, logout } = useAuth();
  
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.getAssignments();
      setAssignments(response.data || []);
    } catch (err) {
      console.error('Failed to load assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAssignmentDetails = async (id: string) => {
    try {
      const response = await apiService.getAssignment(id);
      setAssignments(prev => 
        prev.map(a => a.id === id ? { ...a, attachments: response.data.attachments } : a)
      );
    } catch (err) {
      console.error('Failed to load assignment details:', err);
      alert('Failed to load assignment details');
    }
  };

  const handleToggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      const assignment = assignments.find(a => a.id === id);
      if (assignment && !assignment.attachments) {
        await loadAssignmentDetails(id);
      }
    }
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      setDownloadingId(attachmentId);
      const blob = await apiService.downloadAssignmentAttachment(attachmentId);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      alert('Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'No due date';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isOverdue = (dueAt?: number) => {
    if (!dueAt) return false;
    return dueAt < Date.now();
  };

  const getDaysUntilDue = (dueAt?: number) => {
    if (!dueAt) return null;
    const days = Math.ceil((dueAt - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Sort assignments: upcoming first, then by due date
  const sortedAssignments = [...assignments].sort((a, b) => {
    if (!a.due_at && !b.due_at) return b.created_at - a.created_at;
    if (!a.due_at) return 1;
    if (!b.due_at) return -1;
    return a.due_at - b.due_at;
  });

  if (!user) return null;

  return (
    <Layout
      schoolName={'SMS'}
      principalName={user.loginId || 'Student'}
      onLogout={logout}
      role="student"
    >
      <div style={{ padding: '32px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
            My Assignments
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b' }}>
            View and download assignments from your teachers
          </p>
        </div>

        {error && (
          <div style={{ marginBottom: '24px' }}>
            <Card>
              <div style={{ padding: '24px', textAlign: 'center', color: '#dc2626' }}>
                <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
                <p>{error}</p>
                <Button size="small" variant="secondary" onClick={loadAssignments} style={{ marginTop: '16px' }}>
                  Retry
                </Button>
              </div>
            </Card>
          </div>
        )}

        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} height="150px" />
            ))}
          </div>
        ) : sortedAssignments.length === 0 ? (
          <Card>
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <BookOpen size={64} style={{ color: '#cbd5e1', margin: '0 auto 24px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                No assignments yet
              </h3>
              <p style={{ fontSize: '14px', color: '#64748b' }}>
                Your teachers haven't posted any assignments yet. Check back later!
              </p>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {sortedAssignments.map((assignment) => {
              const overdue = isOverdue(assignment.due_at);
              const daysUntil = getDaysUntilDue(assignment.due_at);
              const isExpanded = expandedId === assignment.id;
              const urgent = daysUntil !== null && daysUntil <= 3 && daysUntil >= 0;

              return (
                <Card key={assignment.id}>
                  <div style={{ padding: '24px' }}>
                    {/* Assignment Header */}
                    <div 
                      onClick={() => handleToggleExpand(assignment.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>
                            {assignment.title}
                          </h3>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <BookOpen size={16} />
                              <span>{assignment.subject_name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <FileText size={16} />
                              <span>By {assignment.teacher_name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Calendar size={16} />
                              <span style={{ 
                                color: overdue ? '#dc2626' : urgent ? '#ea580c' : undefined,
                                fontWeight: overdue || urgent ? 600 : 400
                              }}>
                                {formatDate(assignment.due_at)}
                                {overdue && ' (Overdue)'}
                                {urgent && !overdue && ` (${daysUntil} ${daysUntil === 1 ? 'day' : 'days'} left)`}
                              </span>
                            </div>
                          </div>

                          {assignment.description && !isExpanded && (
                            <p style={{ 
                              fontSize: '14px', 
                              color: '#475569',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {assignment.description}
                            </p>
                          )}

                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Clock size={14} />
                              <span>Posted {new Date(assignment.created_at).toLocaleDateString()}</span>
                            </div>
                            {assignment.attachment_count > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <FileText size={14} />
                                <span>{assignment.attachment_count} attachment{assignment.attachment_count > 1 ? 's' : ''}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ 
                          padding: '8px',
                          background: '#f1f5f9',
                          borderRadius: '6px',
                          marginLeft: '16px'
                        }}>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                        {assignment.description && (
                          <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                              Description
                            </h4>
                            <p style={{ 
                              fontSize: '14px', 
                              color: '#475569', 
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {assignment.description}
                            </p>
                          </div>
                        )}

                        {assignment.attachments && assignment.attachments.length > 0 && (
                          <div>
                            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                              Attachments ({assignment.attachments.length})
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {assignment.attachments.map((attachment) => (
                                <div
                                  key={attachment.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '6px'
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ 
                                      padding: '10px', 
                                      background: '#dbeafe', 
                                      borderRadius: '6px'
                                    }}>
                                      <FileText size={20} style={{ color: '#2563eb' }} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 500 }}>
                                        {attachment.file_name}
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        {formatFileSize(attachment.size_bytes)}
                                      </div>
                                    </div>
                                  </div>

                                  <Button
                                    size="small"
                                    onClick={() => handleDownload(attachment.id, attachment.file_name)}
                                    disabled={downloadingId === attachment.id}
                                  >
                                    <Download size={16} style={{ marginRight: '8px' }} />
                                    {downloadingId === attachment.id ? 'Downloading...' : 'Download'}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
